const BGM_STEP_MS = 250;
const MAX_SFX_VOICES = 18;

interface BgmVoice {
  readonly oscillator: OscillatorNode;
  readonly gain: GainNode;
}

interface SfxVoice {
  readonly oscillator: OscillatorNode;
  readonly gain: GainNode;
}

/**
 * Allocation-bounded procedural soundtrack. No audio files are transferred:
 * three persistent oscillators provide the BGM and short WebAudio voices
 * provide pickup, rare, dash, shift and result cues.
 */
export class AudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private bgmVoices: BgmVoice[] = [];
  private bgmTimer: number | null = null;
  private bgmStep = 0;
  private tierIndex = 0;
  private activeSfxVoices = 0;
  private readonly sfxVoices = new Set<SfxVoice>();
  private enabled = true;
  private paused = false;

  async initialize(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.bgmGain = this.context.createGain();
      this.sfxGain = this.context.createGain();
      this.master.gain.value = this.enabled ? 0.24 : 0;
      this.bgmGain.gain.value = this.paused ? 0 : 0.32;
      this.sfxGain.gain.value = 0.72;
      this.bgmGain.connect(this.master);
      this.sfxGain.connect(this.master);
      this.master.connect(this.context.destination);
      this.createBgm();
    }
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(enabled ? 0.24 : 0, this.context.currentTime, 0.025);
    }
  }

  setPaused(paused: boolean, keepResultSfx = false): void {
    this.paused = paused;
    if (this.bgmGain && this.context) {
      this.bgmGain.gain.setTargetAtTime(paused ? 0 : 0.32, this.context.currentTime, 0.08);
    }
    if (this.sfxGain && this.context) {
      this.sfxGain.gain.setTargetAtTime(
        paused && !keepResultSfx ? 0 : 0.72,
        this.context.currentTime,
        0.025,
      );
    }
  }

  setTier(tierIndex: number): void {
    this.tierIndex = Math.max(0, Math.min(5, Math.floor(tierIndex)));
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  get voiceCount(): number {
    return this.activeSfxVoices;
  }

  get musicActive(): boolean {
    return this.enabled
      && !this.paused
      && this.context?.state === "running"
      && this.bgmTimer !== null
      && this.bgmVoices.length === 3;
  }

  pickup(relativeSize: number, tierIndex: number): void {
    const base = 330 + tierIndex * 38;
    this.tone(base, base + 260 + relativeSize * 420, 0.15, 0.18, "sine");
  }

  rare(): void {
    const notes = [880, 1108.73, 1318.51, 1760, 2217.46];
    notes.forEach((frequency, index) => {
      this.tone(frequency, frequency * 1.035, 0.2, 0.2, "triangle", index * 0.045);
    });
  }

  dash(): void {
    this.tone(190, 720, 0.28, 0.26, "sawtooth");
    this.tone(95, 52, 0.34, 0.18, "triangle");
  }

  dashReady(): void {
    this.tone(740, 1110, 0.14, 0.12, "sine");
  }

  shift(tierIndex: number): void {
    this.setTier(tierIndex);
    for (let note = 0; note < 4; note += 1) {
      const frequency = 110 * 2 ** ((tierIndex + note * 3) / 12);
      this.tone(frequency, frequency * 1.01, 0.58, 0.23, note % 2 === 0 ? "triangle" : "sine", note * 0.075);
    }
  }

  goal(): void {
    [196, 246.94, 293.66, 392].forEach((frequency, index) => {
      this.tone(frequency, frequency * 2, 0.85, 0.2, "triangle", index * 0.07);
    });
  }

  result(rank: string): void {
    const bright = rank === "S" || rank === "A";
    const notes = bright ? [523.25, 659.25, 783.99, 1046.5] : [392, 493.88, 587.33, 783.99];
    notes.forEach((frequency, index) => {
      this.tone(frequency, frequency * 1.005, 0.55, 0.19, "triangle", index * 0.12);
    });
  }

  resetTransientSounds(): void {
    for (const voice of this.sfxVoices) {
      voice.oscillator.onended = null;
      try {
        voice.oscillator.stop();
      } catch {
        // The browser may already have completed a very short cue.
      }
      voice.oscillator.disconnect();
      voice.gain.disconnect();
    }
    this.sfxVoices.clear();
    this.activeSfxVoices = 0;
  }

  dispose(): void {
    if (this.bgmTimer !== null) {
      window.clearInterval(this.bgmTimer);
      this.bgmTimer = null;
    }
    for (const voice of this.bgmVoices) {
      try {
        voice.oscillator.stop();
      } catch {
        // Already stopped during browser teardown.
      }
      voice.oscillator.disconnect();
      voice.gain.disconnect();
    }
    this.bgmVoices.length = 0;
    this.resetTransientSounds();
    this.bgmGain?.disconnect();
    this.sfxGain?.disconnect();
    this.master?.disconnect();
    const context = this.context;
    this.master = null;
    this.bgmGain = null;
    this.sfxGain = null;
    this.context = null;
    if (context && context.state !== "closed") {
      void context.close().catch(() => undefined);
    }
  }

  private createBgm(): void {
    if (!this.context || !this.bgmGain) return;
    const types: OscillatorType[] = ["triangle", "sine", "sine"];
    const levels = [0.075, 0.045, 0.065];
    for (let index = 0; index < 3; index += 1) {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = types[index] ?? "sine";
      oscillator.frequency.value = index === 2 ? 110 : 220;
      gain.gain.value = levels[index] ?? 0.04;
      oscillator.connect(gain);
      gain.connect(this.bgmGain);
      oscillator.start();
      this.bgmVoices.push({ oscillator, gain });
    }
    this.scheduleBgmStep();
    this.bgmTimer = window.setInterval(() => this.scheduleBgmStep(), BGM_STEP_MS);
  }

  private scheduleBgmStep(): void {
    if (!this.context || this.bgmVoices.length < 3) return;
    const roots = [220, 233.08, 246.94, 261.63, 277.18, 293.66];
    const pattern = [0, 4, 7, 11, 7, 4, 2, 7];
    const root = roots[this.tierIndex] ?? 220;
    const interval = pattern[this.bgmStep % pattern.length] ?? 0;
    const time = this.context.currentTime;
    const lead = this.bgmVoices[0];
    const harmony = this.bgmVoices[1];
    const bass = this.bgmVoices[2];
    lead?.oscillator.frequency.setTargetAtTime(root * 2 ** (interval / 12), time, 0.018);
    harmony?.oscillator.frequency.setTargetAtTime(root * 2 ** ((interval + 7) / 12), time, 0.03);
    bass?.oscillator.frequency.setTargetAtTime(root * 0.5 * 2 ** ((this.bgmStep % 4 === 3 ? 7 : 0) / 12), time, 0.04);
    if (lead) lead.gain.gain.setTargetAtTime(this.bgmStep % 2 === 0 ? 0.09 : 0.052, time, 0.025);
    this.bgmStep = (this.bgmStep + 1) % 32;
  }

  private tone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    level: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    if (
      !this.enabled
      || !this.context
      || !this.sfxGain
      || this.activeSfxVoices >= MAX_SFX_VOICES
    ) {
      return;
    }
    const now = this.context.currentTime + Math.max(0, delay);
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(20, startFrequency), now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.sfxGain);
    const voice = { oscillator, gain };
    this.sfxVoices.add(voice);
    this.activeSfxVoices += 1;
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
      this.sfxVoices.delete(voice);
      this.activeSfxVoices = Math.max(0, this.activeSfxVoices - 1);
    };
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }
}
